import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Field,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { relativeTime } from '@/utils/format';

/**
 * What the platform is wired to, and — for the vendor APIs — changing it.
 *
 * Three things this screen has to keep straight, because getting any of them
 * wrong turns a settings page into a support ticket:
 *
 * - **Where a value came from.** The deployment supplies a baseline and this
 *   screen supplies an overlay on top of it. Every field says which is winning,
 *   or "I changed it and nothing happened" has no answer.
 * - **A secret is write-only.** It is never sent to the browser, not even
 *   masked, so the field cannot be pre-filled and an empty box means "keep what
 *   is stored" rather than "clear it". The form says so rather than hoping.
 * - **Infrastructure is shown and locked.** Storage and Redis are here with the
 *   reason they cannot be edited, because hiding them would just make somebody
 *   go looking for the screen that does have them.
 */
export function SeamSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [reverting, setReverting] = useState(null);
  const [tested, setTested] = useState({});

  const seams = useQuery({
    queryKey: ['integration-settings'],
    queryFn: adminApi.getIntegrationSettings,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['integration-settings'] });

  const save = useMutation({
    mutationFn: ({ seam, values }) => adminApi.saveIntegrationSeam(seam, values),
    onSuccess: () => {
      // Deliberately says "this worker" rather than implying the change is
      // everywhere: other processes re-read within the minute, and a message
      // that promised otherwise would be wrong for up to a minute.
      toast.success('Saved. Other workers pick this up within a minute.');
      setEditing(null);
      void refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save that')),
  });

  const revert = useMutation({
    mutationFn: (seam) => adminApi.revertIntegrationSeam(seam),
    onSuccess: () => {
      toast.success('Back to the deployment’s own settings');
      setReverting(null);
      void refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const test = useMutation({
    mutationFn: (seam) => adminApi.testIntegrationSeam(seam),
    onSuccess: (result) => {
      setTested((previous) => ({ ...previous, [result.seam]: result }));
      if (result.ok) toast.success(`Answered in ${result.elapsed_ms} ms`);
      else toast.error(result.detail);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = seams.data?.seams ?? [];

  return (
    <Card className="mb-6">
      <CardHeader
        title="Third-party services"
        description="What this deployment is wired to. The deployment’s environment is the baseline; anything changed here sits on top of it and can be reverted."
      />

      {seams.isPending ? (
        <LoadingState label="Reading configuration…" />
      ) : seams.isError ? (
        <Alert tone="error" title="Could not read the configuration">
          <p>{errorMessage(seams.error)}</p>
          <Button
            variant="secondary"
            size="xs"
            className="mt-2"
            onClick={() => void seams.refetch()}
          >
            Try again
          </Button>
        </Alert>
      ) : (
        <ul className="divide-line divide-y">
          {rows.map((seam) => (
            <li key={seam.key} className="flex flex-wrap items-start gap-3 py-4">
              <div className="min-w-48 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-ink text-sm font-medium">{seam.label}</p>
                  {/* Configured and working are different things here. A seam
                      with a fallback is not broken when nothing is set, and one
                      without a fallback is. */}
                  {seam.configured ? (
                    <Badge tone="success">{seam.active}</Badge>
                  ) : seam.fallback ? (
                    <Badge tone="neutral">{seam.active}</Badge>
                  ) : (
                    <Badge tone="warning">{seam.active}</Badge>
                  )}
                  {seam.overridden && <Badge tone="brand">Set here</Badge>}
                </div>
                <p className="text-ink-muted text-xs">{seam.purpose}</p>
              </div>

              <div className="text-ink-muted min-w-56 flex-1 text-xs">
                {seam.detail && <p>{seam.detail}</p>}
                {!seam.configured && seam.fallback && <p>Falling back to: {seam.fallback}</p>}
                {seam.available.length > 0 && (
                  <p>This build can use: {seam.available.join(', ')}</p>
                )}
                {seam.overridden && seam.updated_at && (
                  <p>Changed here {relativeTime(seam.updated_at)}</p>
                )}
                {/* The reason lives next to the thing it explains. A locked row
                    with no explanation reads as an oversight. */}
                {!seam.editable && seam.locked_reason && <p>{seam.locked_reason}</p>}
                {tested[seam.key] && (
                  <p className={tested[seam.key].ok ? 'text-success-700' : 'text-danger-600'}>
                    {tested[seam.key].ok ? 'Answered' : 'No answer'} · {tested[seam.key].elapsed_ms}{' '}
                    ms · {tested[seam.key].detail}
                  </p>
                )}
              </div>

              {seam.editable && (
                <div className="flex flex-wrap items-center gap-1">
                  <IconButton
                    icon="settings"
                    label={`Configure ${seam.label}`}
                    variant="ghostBrand"
                    onClick={() => setEditing(seam)}
                  />
                  <IconButton
                    icon="zap"
                    label={`Test ${seam.label}`}
                    isLoading={test.isPending && test.variables === seam.key}
                    onClick={() => test.mutate(seam.key)}
                  />
                  {seam.overridden && (
                    <IconButton
                      icon="reset"
                      label={`Revert ${seam.label}`}
                      variant="ghostDanger"
                      onClick={() => setReverting(seam)}
                    />
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-ink-muted mt-4 text-xs">
        Whether a service is answering <em>right now</em> is what <strong>Test</strong> asks — this
        list never calls out to draw itself, or it would hang exactly when one was down.
      </p>

      <SeamDialog
        seam={editing}
        isSaving={save.isPending}
        onCancel={() => setEditing(null)}
        onSave={(values) => save.mutate({ seam: editing.key, values })}
      />

      <ConfirmDialog
        open={Boolean(reverting)}
        title={`Revert ${reverting?.label ?? ''}?`}
        message={`Everything set here is dropped and ${reverting?.label ?? 'this service'} goes back to what the deployment supplies. ${reverting?.consequence ?? ''}`}
        confirmLabel="Revert"
        tone="primary"
        isLoading={revert.isPending}
        onCancel={() => setReverting(null)}
        onConfirm={() => revert.mutate(reverting.key)}
      />
    </Card>
  );
}

/**
 * One seam's form.
 *
 * Seeded once per opening from what the server reports, which for a secret is
 * nothing at all — so a secret input starts empty and stays empty unless
 * somebody types a replacement.
 */
function SeamDialog({ seam, isSaving, onCancel, onSave }) {
  const [form, setForm] = useState({});
  const [clearing, setClearing] = useState({});
  const [ready, setReady] = useState(null);

  if (seam && ready !== seam) {
    setReady(seam);
    setClearing({});
    setForm(
      Object.fromEntries(
        seam.fields.map((item) => [item.key, item.secret ? '' : (item.value ?? '')]),
      ),
    );
  }
  if (!seam) {
    if (ready !== null) setReady(null);
    return null;
  }

  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const payload = () => {
    const values = {};
    for (const item of seam.fields) {
      if (!item.secret) {
        values[item.key] = item.kind === 'bool' ? Boolean(form[item.key]) : form[item.key];
        continue;
      }
      // Absent means keep. Only a typed replacement or an explicit removal
      // says anything about a stored credential.
      const typed = (form[item.key] ?? '').trim();
      if (typed) values[item.key] = typed;
      else if (clearing[item.key]) values[item.key] = '';
    }
    return values;
  };

  return (
    <Modal
      open
      title={seam.label}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button isLoading={isSaving} onClick={() => onSave(payload())}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* What a wrong value costs, before the inputs rather than after. It is
            what decides whether somebody edits this on a Friday afternoon. */}
        <p className="text-ink-muted text-sm">If this is wrong: {seam.consequence}</p>

        {seam.fields.map((item) => (
          <SeamField
            key={item.key}
            item={item}
            value={form[item.key]}
            clearing={Boolean(clearing[item.key])}
            onChange={(value) => set(item.key, value)}
            onClearing={(value) => setClearing((previous) => ({ ...previous, [item.key]: value }))}
          />
        ))}

        <p className="text-ink-muted text-xs">
          Saved here, these override what the deployment’s environment supplies. Reverting puts the
          environment back — it is still there underneath.
        </p>
      </div>
    </Modal>
  );
}

function SeamField({ item, value, clearing, onChange, onClearing }) {
  const source = item.source === 'console' ? 'Set here' : 'From the deployment’s environment';
  const hint = [item.help, source].filter(Boolean).join(' · ');

  if (item.kind === 'bool') {
    return (
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          <span className="text-ink font-medium">{item.label}</span>
          <span className="text-ink-muted block text-xs">{hint}</span>
        </span>
      </label>
    );
  }

  if (item.kind === 'select') {
    return (
      <Select
        label={item.label}
        hint={hint}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Not configured</option>
        {item.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  if (item.secret) {
    return (
      <Field
        label={item.label}
        hint={
          item.is_set
            ? `A key is saved. Leave blank to keep it. · ${source}`
            : `Nothing saved. · ${source}`
        }
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={value ?? ''}
          placeholder={item.is_set ? 'Leave blank to keep the saved key' : (item.placeholder ?? '')}
          disabled={clearing}
          onChange={(event) => onChange(event.target.value)}
        />
        {/* The only way to remove a stored credential, so it is a control
            rather than a convention somebody has to know. */}
        {item.is_set && (
          <label className="text-ink-muted mt-2 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={clearing}
              onChange={(event) => onClearing(event.target.checked)}
            />
            Remove the saved key
          </label>
        )}
      </Field>
    );
  }

  return (
    <Field label={item.label} hint={hint}>
      <Input
        value={value ?? ''}
        placeholder={item.placeholder ?? ''}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}
